/**
 * Chest screen — Minecraft-style storage UI, click-first:
 * click an item in the chest to take it, click an item in your hotbar
 * to store it. Same in-game visual language as the inventory screen.
 */

import type { Inventory } from "@craftgaming/shared";
import { BLOCK_NAMES, getSlotIcon } from "./hotbar";

const STYLE = `
#chest-screen { position: fixed; inset: 0; background: rgba(10,10,18,0.62); z-index: 145; display: none; font-family: 'Segoe UI', system-ui, sans-serif; color: #e0e0e0; }
.ch-panel { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: min(460px, 94vw); background: #2b2b33; border: 3px solid; border-color: #6e6e78 #1c1c22 #1c1c22 #6e6e78; box-shadow: 0 0 0 3px #0008, 0 10px 40px #000a; padding: 8px 10px 10px; }
.ch-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.ch-title { font-size: 0.9rem; font-weight: 800; color: #ffd766; text-shadow: 1px 1px 0 #000; }
.ch-close { width: 34px; height: 34px; border: 2px solid; border-color: #6e6e78 #1c1c22 #1c1c22 #6e6e78; background: #3a3a44; color: #ddd; font-size: 0.95rem; cursor: pointer; font-weight: 800; }
.ch-label { font-size: 0.65rem; font-weight: 800; color: #999; text-shadow: 1px 1px 0 #000; margin: 6px 0 3px; text-transform: uppercase; letter-spacing: 1px; }
.ch-grid { display: grid; grid-template-columns: repeat(9, 1fr); gap: 3px; }
.ch-slot { aspect-ratio: 1; min-width: 0; background: #232329; border: 2px solid; border-color: #17171d #55555f #55555f #17171d; display: flex; align-items: center; justify-content: center; position: relative; cursor: pointer; padding: 0; }
.ch-slot:hover { background: #2e2e38; }
.ch-slot:active { border-color: #55555f #17171d #17171d #55555f; }
.ch-slot img { width: 70%; height: 70%; image-rendering: pixelated; pointer-events: none; }
.ch-slot .count { position: absolute; bottom: 0; right: 2px; font-size: 10px; font-weight: bold; font-family: monospace; text-shadow: 0 1px 2px #000; pointer-events: none; }
.ch-hint { text-align: center; margin-top: 6px; font-size: 0.62rem; color: #777; text-shadow: 1px 1px 0 #000; }
`;

export type ChestScreen = {
  /** Opens the UI bound to a chest inventory. */
  open: (chest: Inventory, playerInventory: Inventory) => void;
  hide: () => void;
  isVisible: () => boolean;
  dispose: () => void;
};

export function createChestScreen(callbacks: {
  onShow: () => void;
  onHide: () => void;
  /** Called after any transfer (refresh hotbar etc.). */
  onChanged: () => void;
}): ChestScreen {
  let visible = false;
  let chest: Inventory | null = null;
  let player: Inventory | null = null;

  const style = document.createElement("style");
  style.textContent = STYLE;
  document.head.appendChild(style);

  const overlay = document.createElement("div");
  overlay.id = "chest-screen";
  overlay.addEventListener("pointerdown", (e) => {
    if (e.target === overlay) hide();
  });

  const panel = document.createElement("div");
  panel.className = "ch-panel";
  overlay.appendChild(panel);

  const header = document.createElement("div");
  header.className = "ch-header";
  const title = document.createElement("div");
  title.className = "ch-title";
  title.textContent = "📦 Baú";
  const closeButton = document.createElement("button");
  closeButton.className = "ch-close";
  closeButton.textContent = "✕";
  closeButton.setAttribute("aria-label", "Fechar baú");
  closeButton.addEventListener("click", () => hide());
  header.append(title, closeButton);
  panel.appendChild(header);

  const chestLabel = document.createElement("div");
  chestLabel.className = "ch-label";
  chestLabel.textContent = "Baú — clique para pegar";
  panel.appendChild(chestLabel);

  const chestGrid = document.createElement("div");
  chestGrid.className = "ch-grid";
  panel.appendChild(chestGrid);

  const playerLabel = document.createElement("div");
  playerLabel.className = "ch-label";
  playerLabel.textContent = "Seus itens — clique para guardar";
  panel.appendChild(playerLabel);

  const playerGrid = document.createElement("div");
  playerGrid.className = "ch-grid";
  panel.appendChild(playerGrid);

  const hint = document.createElement("div");
  hint.className = "ch-hint";
  hint.textContent = "E ou ESC fecham · quebrar o baú derruba os itens no chão";
  panel.appendChild(hint);

  document.body.appendChild(overlay);

  function renderGrid(grid: HTMLElement, inventory: Inventory, onSlotClick: (index: number) => void) {
    grid.innerHTML = "";
    for (let i = 0; i < inventory.items.length; i++) {
      const item = inventory.items[i];
      const slot = document.createElement("button");
      slot.className = "ch-slot";
      if (item && item.count > 0) {
        const name = BLOCK_NAMES[item.blockId] || `Item ${item.blockId}`;
        slot.setAttribute("aria-label", `${name} ×${item.count}`);
        slot.innerHTML = `
          <img src="${getSlotIcon(item.blockId, 30)}" alt="" />
          ${item.count > 1 ? `<span class="count">${item.count}</span>` : ""}
        `;
        const index = i;
        slot.addEventListener("click", () => onSlotClick(index));
      }
      grid.appendChild(slot);
    }
  }

  function render() {
    if (!chest || !player) return;
    const chestInventory = chest;
    const playerInventory = player;

    // Chest slot click → move the stack to the player (as much as fits)
    renderGrid(chestGrid, chestInventory, (index) => {
      const item = chestInventory.items[index];
      if (!item) return;
      const moved = transfer(chestInventory, playerInventory, index);
      if (moved) {
        render();
        callbacks.onChanged();
      }
    });

    // Player slot click → store the stack in the chest
    renderGrid(playerGrid, playerInventory, (index) => {
      const item = playerInventory.items[index];
      if (!item) return;
      const moved = transfer(playerInventory, chestInventory, index);
      if (moved) {
        render();
        callbacks.onChanged();
      }
    });
  }

  /** Moves as much of the stack at `index` as fits. Never destroys items. */
  function transfer(from: Inventory, to: Inventory, index: number): boolean {
    const item = from.items[index];
    if (!item || item.count <= 0) return false;

    // Move only what fits — canFit-first keeps this loss-proof
    let movable = item.count;
    while (movable > 0 && !to.canFit(item.blockId, movable)) movable--;
    if (movable <= 0) return false;

    to.addItem(item.blockId, movable);
    item.count -= movable;
    if (item.count <= 0) from.items[index] = null;
    return true;
  }

  function open(chestInventory: Inventory, playerInventory: Inventory) {
    chest = chestInventory;
    player = playerInventory;
    visible = true;
    render();
    overlay.style.display = "block";
    callbacks.onShow();
  }

  function hide() {
    if (!visible) return;
    visible = false;
    chest = null;
    player = null;
    overlay.style.display = "none";
    callbacks.onHide();
  }

  return {
    open,
    hide,
    isVisible: () => visible,
    dispose() {
      overlay.remove();
      style.remove();
    },
  };
}
